import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Activity as ActivityIcon, Download, Loader2, AlertTriangle, CheckCircle2,
  Mail, Settings as SettingsIcon, Receipt, Plus, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const ICONS = {
  daily_report: Clock, task: CheckCircle2, project: CheckCircle2, client: Plus,
  lead: Plus, message_thread: Mail, user: SettingsIcon, invoice: Receipt,
  content: CheckCircle2,
};
const TYPE_LABELS = {
  all: 'All types', daily_report: 'Work logged', task: 'Tasks', project: 'Projects',
  client: 'Clients', lead: 'Leads', message_thread: 'Messages', user: 'Access changes',
  invoice: 'Finance', content: 'Content', change_request: 'Change requests', sop: 'SOPs',
};
const WINDOWS = [
  { key: '1', days: 1, label: 'Today' },
  { key: '7', days: 7, label: 'Last 7 days' },
  { key: '30', days: 30, label: 'Last 30 days' },
  { key: '90', days: 90, label: 'Last 90 days' },
];

// Defensive normaliser — one malformed row must never break the whole list.
// Any missing or oddly-typed field falls back to a safe default so the
// renderer stays total.
function safeRow(r, idx) {
  const row = r || {};
  const et = typeof row.entity_type === 'string' ? row.entity_type : 'other';
  const createdAt = typeof row.created_at === 'string' ? row.created_at : '';
  return {
    id: row.id || `row-${idx}-${createdAt || Math.random().toString(36).slice(2, 10)}`,
    entity_type: et,
    created_at: createdAt,
    actor_name: row.actor_name || 'Someone',
    actor_role: row.actor_role || '',
    description: typeof row.description === 'string' ? row.description : (row.action || 'made a change'),
    extra: (row.extra && typeof row.extra === 'object') ? row.extra : {},
    _raw: row,
  };
}

const dayKey = (iso) => (iso || '').slice(0, 10);
const niceDay = (key) => {
  if (!key) return '';
  const d = new Date(key); const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - d) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};
const niceTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';

class ActivityErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null, info: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    // Log the real stack trace so admins see the exact line in DevTools.
    // Keep the user-facing card clean.
    // eslint-disable-next-line no-console
    console.error('[ActivityPage] render crashed', err, info?.componentStack);
    this.setState({ info });
  }
  render() {
    if (this.state.err) {
      return (
        <div className="max-w-xl mx-auto py-12">
          <Card className="shine-card p-8 rounded-xl text-center">
            <AlertTriangle className="h-10 w-10 text-red-600 mx-auto" />
            <h2 className="text-xl font-bold mt-3 text-slate-900">Couldn't load activity</h2>
            <p className="text-sm text-slate-500 mt-1">
              A client-side error prevented the page from rendering. Full stack is in the DevTools console.
            </p>
            <div className="text-[11px] text-slate-400 mt-2 font-mono">
              {String(this.state.err?.message || this.state.err).slice(0, 180)}
            </div>
            <Button className="mt-4" onClick={() => window.location.reload()}>Reload</Button>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

function ActivityPageInner() {
  const { isOwner } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [totals, setTotals] = useState({ total: 0, actors: 0, by_type: [] });
  const [windowKey, setWindowKey] = useState('7');
  const [typeFilter, setTypeFilter] = useState('all');
  const timeoutRef = useRef(null);

  const dateFrom = (() => {
    const w = WINDOWS.find((x) => x.key === windowKey) || WINDOWS[1];
    const d = new Date(); d.setDate(d.getDate() - w.days);
    return d.toISOString();
  })();

  const load = async ({ reset = true } = {}) => {
    setLoading(true); setError(null);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setError({ status: 504, ref: 'client-timeout', msg: "The server didn't respond. Your data is safe — nothing was changed. Check your connection and try again." });
      setLoading(false);
    }, 15000);
    try {
      const res = await api.get('/activity/feed', {
        params: {
          date_from: dateFrom,
          entity_type: typeFilter === 'all' ? undefined : typeFilter,
          cursor: reset ? undefined : cursor,
          limit: 30,
        },
      });
      clearTimeout(timeoutRef.current);
      const raw = Array.isArray(res.data?.entries) ? res.data.entries : [];
      // Normalise every row so a malformed one can't crash the renderer.
      const newEntries = raw.map((r, i) => safeRow(r, i));
      // Functional updater avoids a stale `entries` closure if a filter
      // change triggers a reload before this response resolves.
      setEntries((prev) => (reset ? newEntries : [...prev, ...newEntries]));
      setHasMore(!!res.data?.has_more);
      setCursor(res.data?.next_cursor || null);
      setTotals({ total: res.data?.total || 0, actors: res.data?.actors || 0, by_type: res.data?.by_type || [] });
    } catch (e) {
      clearTimeout(timeoutRef.current);
      setError({
        status: e?.response?.status || 500,
        ref: e?.response?.headers?.['x-request-id'] || '—',
        msg: "The server didn't respond. Your data is safe — nothing was changed. Check your connection and try again.",
      });
    } finally { setLoading(false); }
  };

  useEffect(() => { load({ reset: true }); return () => clearTimeout(timeoutRef.current);
    // eslint-disable-next-line
  }, [windowKey, typeFilter]);

  const exportCsv = async () => {
    try {
      const res = await api.get('/activity/export', {
        params: { date_from: dateFrom, entity_type: typeFilter === 'all' ? undefined : typeFilter },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'shine_activity_log.csv';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    }
  };

  // Group entries by day for the "TODAY / YESTERDAY" headers.
  const grouped = (() => {
    const g = {};
    for (const r of entries) {
      const k = dayKey(r.created_at);
      (g[k] = g[k] || []).push(r);
    }
    return Object.entries(g).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  })();

  if (loading && entries.length === 0) {
    return (
      <div className="space-y-4" data-testid="activity-loading">
        <h1 className="text-2xl font-extrabold text-slate-900">Activity</h1>
        <p className="text-xs text-slate-500">Everything that changed in the Ops Hub, newest first.</p>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading activity…
        </div>
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto py-12" data-testid="activity-error">
        <Card className="shine-card p-8 rounded-xl text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-red-50 border border-red-200 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mt-3">Couldn't load activity</h2>
          <p className="text-sm text-slate-500 mt-1">{error.msg}</p>
          <div className="mt-4 flex gap-2 justify-center">
            <Button onClick={() => load({ reset: true })} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="try-again">
              Try again
            </Button>
            <Button variant="outline" onClick={() => navigate('/')}>Go to dashboard</Button>
          </div>
          <div className="text-[11px] text-slate-400 mt-3">Error code {error.status} · ref {error.ref}</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="activity-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-slate-500">Settings › Activity log</div>
          <h1 className="text-2xl font-extrabold text-slate-900">Activity</h1>
          <p className="text-xs text-slate-500">Everything that changed in the Ops Hub, newest first.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[150px]" data-testid="type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={windowKey} onValueChange={setWindowKey}>
            <SelectTrigger className="w-[150px]" data-testid="window-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {WINDOWS.map((w) => <SelectItem key={w.key} value={w.key}>{w.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {isOwner && (
            <Button onClick={exportCsv} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="export-csv">
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="space-y-3">
          {grouped.length === 0 ? (
            <Card className="shine-card p-10 rounded-xl text-center text-sm text-slate-500">
              No activity in this period.
            </Card>
          ) : (
            grouped.map(([day, rows]) => (
              <div key={day}>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-3 mb-2">{niceDay(day)} · {day}</div>
                <div className="space-y-2">
                  {rows.map((r) => {
                    const Icon = ICONS[r.entity_type] || ActivityIcon;
                    return (
                      <Card key={r.id} className="shine-card px-3 py-2 rounded-lg flex items-start gap-3">
                        <div className="h-7 w-7 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
                          <Icon className="h-3.5 w-3.5 text-slate-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-slate-800">
                            <b>{r.actor_name}</b> {r.description}
                          </div>
                          <div className="text-xs text-slate-500">
                            {TYPE_LABELS[r.entity_type] || r.entity_type}
                            {r.extra?.client_name ? ` · ${r.extra.client_name}` : ''}
                          </div>
                        </div>
                        <div className="text-[11px] text-slate-400 flex-shrink-0">{niceTime(r.created_at)}</div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))
          )}
          {hasMore && (
            <div className="pt-2 text-center">
              <Button variant="outline" onClick={() => load({ reset: false })} data-testid="load-more">
                {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Load older activity
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <Card className="shine-card p-4 rounded-xl bg-slate-900 text-white">
            <div className="text-xs uppercase tracking-wider text-slate-300">In window</div>
            <div className="text-3xl font-extrabold mt-1">{totals.total}</div>
            <div className="text-xs text-slate-400 mt-1">actions by {totals.actors} people</div>
          </Card>
          <Card className="shine-card p-4 rounded-xl">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">By type</div>
            <div className="space-y-1 text-sm">
              {(totals.by_type || []).map((t) => (
                <div key={t.entity_type} className="flex items-center justify-between">
                  <span className="text-slate-700">{TYPE_LABELS[t.entity_type] || t.entity_type}</span>
                  <span className="font-semibold text-slate-800">{t.count}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default function ActivityPage() {
  return (
    <ActivityErrorBoundary>
      <ActivityPageInner />
    </ActivityErrorBoundary>
  );
}
